import type { PopupSurfaceState } from "@/surfaces/SurfaceTypes";
import type { EngagementProvider } from "@/provider/EngagementProvider";
import type { ProviderRuntimeSnapshot } from "@/provider/ProviderTypes";
import type { ShellPopupPhase } from "@/shell/ShellRuntimeTypes";
import type {
	ShellRuntimeAction,
	ShellRuntimeState,
} from "@/shell/ShellRuntimeReducer";

export type PopupDesiredPhaseDecision =
	| {
			kind: "close";
			nextPhase: ShellPopupPhase;
	  }
	| {
			kind: "request_fresh_boot";
			expectedFreshBootGeneration: number;
			nextPendingPopupGeneration: number;
	  }
	| {
			kind: "open_now";
			nextPhase: "opening" | "open";
	  }
	| {
			kind: "wait_provider";
			nextPendingPopupGeneration: number;
	  };

export type ResolveDesiredPopupPhaseInput = {
	popupDesiredOpen: boolean;
	freshBootRequiredBeforeNextOpen: boolean;

	provider: EngagementProvider | null;
	providerReady: boolean;
	providerRuntimeSnapshot: ProviderRuntimeSnapshot;

	activeBootGeneration: number;
	readyBootGeneration: number | null;

	popupState: PopupSurfaceState | null;
};

export type ResolvePopupControllerInput = {
	state: ShellRuntimeState;

	provider: EngagementProvider | null;
	providerReady: boolean;
	providerRuntimeSnapshot: ProviderRuntimeSnapshot;

	activeBootGeneration: number;
	readyBootGeneration: number | null;
};

export type PopupControllerResolution =
	| {
			kind: "noop";
	  }
	| {
			kind: "dispatch";
			action: ShellRuntimeAction;
			reason:
				| "desired_close"
				| "desired_request_fresh_boot"
				| "desired_open_now"
				| "desired_wait_provider"
				| "pending_generation_ready"
				| "opening_granted"
				| "closing_ack";
	  };

function canOpenPopupForGeneration(
	provider: EngagementProvider | null,
	providerReady: boolean,
	providerRuntimeSnapshot: ProviderRuntimeSnapshot,
	activeBootGeneration: number,
	readyBootGeneration: number | null,
	expectedBootGeneration: number
): boolean {
	return (
		providerReady &&
		!!provider &&
		providerRuntimeSnapshot.phase === "ready" &&
		activeBootGeneration === expectedBootGeneration &&
		readyBootGeneration === expectedBootGeneration
	);
}

export function resolveCloseTargetPhase(
	popupState: PopupSurfaceState | null
): ShellPopupPhase {
	return popupState?.granted ? "closing" : "closed";
}

export function resolveDesiredPopupPhase(
	input: ResolveDesiredPopupPhaseInput
): PopupDesiredPhaseDecision {
	if (!input.popupDesiredOpen) {
		return {
			kind: "close",
			nextPhase: resolveCloseTargetPhase(input.popupState),
		};
	}

	if (input.freshBootRequiredBeforeNextOpen) {
		const expectedFreshBootGeneration = input.activeBootGeneration + 1;

		return {
			kind: "request_fresh_boot",
			expectedFreshBootGeneration,
			nextPendingPopupGeneration: expectedFreshBootGeneration,
		};
	}

	if (
		canOpenPopupForGeneration(
			input.provider,
			input.providerReady,
			input.providerRuntimeSnapshot,
			input.activeBootGeneration,
			input.readyBootGeneration,
			input.activeBootGeneration
		)
	) {
		return {
			kind: "open_now",
			nextPhase: input.popupState?.granted ? "open" : "opening",
		};
	}

	return {
		kind: "wait_provider",
		nextPendingPopupGeneration: input.activeBootGeneration,
	};
}

export function resolvePendingPopupReadyTransition(
	popupDesiredOpen: boolean,
	pendingPopupGeneration: number | null,
	provider: EngagementProvider | null,
	providerReady: boolean,
	providerRuntimeSnapshot: ProviderRuntimeSnapshot,
	activeBootGeneration: number,
	readyBootGeneration: number | null,
	popupState: PopupSurfaceState | null
): { nextPhase: "opening" | "open" } | null {
	if (!popupDesiredOpen) {
		return null;
	}

	if (pendingPopupGeneration === null) {
		return null;
	}

	if (
		!canOpenPopupForGeneration(
			provider,
			providerReady,
			providerRuntimeSnapshot,
			activeBootGeneration,
			readyBootGeneration,
			pendingPopupGeneration
		)
	) {
		return null;
	}

	return {
		nextPhase: popupState?.granted ? "open" : "opening",
	};
}

export function resolveOpeningGrantedTransition(
	popupPhase: ShellPopupPhase,
	popupState: PopupSurfaceState | null
): "open" | null {
	if (popupPhase !== "opening") {
		return null;
	}

	if (!popupState?.granted) {
		return null;
	}

	return "open";
}

export function resolveClosingAckTransition(
	popupPhase: ShellPopupPhase,
	popupState: PopupSurfaceState | null
): "closed" | null {
	if (popupPhase !== "closing") {
		return null;
	}

	if (popupState?.granted) {
		return null;
	}

	return "closed";
}

export function resolvePopupControllerAction(
	input: ResolvePopupControllerInput
): PopupControllerResolution {
	const pendingReadyTransition = resolvePendingPopupReadyTransition(
		input.state.popupDesiredOpen,
		input.state.pendingPopupGeneration,
		input.provider,
		input.providerReady,
		input.providerRuntimeSnapshot,
		input.activeBootGeneration,
		input.readyBootGeneration,
		input.state.popupState
	);

	if (pendingReadyTransition) {
		return {
			kind: "dispatch",
			reason: "pending_generation_ready",
			action: {
				type: "PROMOTE_PENDING_POPUP_TO_OPEN",
				payload: {
					nextPhase: pendingReadyTransition.nextPhase,
				},
			},
		};
	}

	const openingGrantedTransition = resolveOpeningGrantedTransition(
		input.state.popupPhase,
		input.state.popupState
	);

	if (openingGrantedTransition) {
		return {
			kind: "dispatch",
			reason: "opening_granted",
			action: {
				type: "SET_POPUP_PHASE",
				payload: openingGrantedTransition,
			},
		};
	}

	const closingAckTransition = resolveClosingAckTransition(
		input.state.popupPhase,
		input.state.popupState
	);

	if (closingAckTransition) {
		return {
			kind: "dispatch",
			reason: "closing_ack",
			action: {
				type: "SET_POPUP_PHASE",
				payload: closingAckTransition,
			},
		};
	}

	const desiredDecision = resolveDesiredPopupPhase({
		popupDesiredOpen: input.state.popupDesiredOpen,
		freshBootRequiredBeforeNextOpen:
			input.state.freshBootRequiredBeforeNextOpen,
		provider: input.provider,
		providerReady: input.providerReady,
		providerRuntimeSnapshot: input.providerRuntimeSnapshot,
		activeBootGeneration: input.activeBootGeneration,
		readyBootGeneration: input.readyBootGeneration,
		popupState: input.state.popupState,
	});

	switch (desiredDecision.kind) {
		case "close": {
			const shouldClose =
				input.state.pendingPopupGeneration !== null ||
				input.state.popupPhase !== desiredDecision.nextPhase;

			if (!shouldClose) {
				return {
					kind: "noop",
				};
			}

			return {
				kind: "dispatch",
				reason: "desired_close",
				action: {
					type: "CLEAR_PENDING_POPUP_AND_CLOSE",
					payload: {
						nextPhase: desiredDecision.nextPhase,
					},
				},
			};
		}

		case "request_fresh_boot": {
			const shouldPrepareFreshBoot =
				input.state.pendingPopupGeneration !==
					desiredDecision.nextPendingPopupGeneration ||
				input.state.popupPhase !== "waiting_fresh_boot" ||
				input.state.freshBootRequiredBeforeNextOpen;

			if (!shouldPrepareFreshBoot) {
				return {
					kind: "noop",
				};
			}

			return {
				kind: "dispatch",
				reason: "desired_request_fresh_boot",
				action: {
					type: "PREPARE_FRESH_BOOT_FOR_POPUP_OPEN",
					payload: {
						nextExpectedGeneration:
							desiredDecision.expectedFreshBootGeneration,
					},
				},
			};
		}

		case "open_now": {
			const shouldOpenNow =
				input.state.pendingPopupGeneration !== null ||
				input.state.popupPhase !== desiredDecision.nextPhase;

			if (!shouldOpenNow) {
				return {
					kind: "noop",
				};
			}

			return {
				kind: "dispatch",
				reason: "desired_open_now",
				action: {
					type: "OPEN_POPUP_NOW",
					payload: {
						nextPhase: desiredDecision.nextPhase,
					},
				},
			};
		}

		case "wait_provider": {
			const shouldWait =
				input.state.pendingPopupGeneration !==
					desiredDecision.nextPendingPopupGeneration ||
				input.state.popupPhase !== "waiting_provider";

			if (!shouldWait) {
				return {
					kind: "noop",
				};
			}

			return {
				kind: "dispatch",
				reason: "desired_wait_provider",
				action: {
					type: "WAIT_FOR_PROVIDER",
					payload: {
						generation: desiredDecision.nextPendingPopupGeneration,
					},
				},
			};
		}
	}
}